// Implements: F-RPT-007, F-UIX-018, F-UIX-010
/**
 * Mockup v3 P9 Consumo "Actividad del mes" for an organization's admin: this month so far and the
 * window before it (`previousWindow`), both through `GET /reports/organizations/{org}/activity`.
 *
 * Mounted only for an organization target (the report is the organization's; a team lead asks
 * nothing). The reads are cached by organization and window (`orgActivityKey`, checklist 14), so a
 * late answer of another organization never lands here. The day the month is counted from is
 * handed in (checklist 16). The month's own failure is the error state with its retry; the window
 * before only feeds the comparison line, which is not drawn while it is missing.
 *
 * Owner 2026-10-19: a third read, the whole month before (`wholeMonthBefore`), feeds its "completo"
 * lines the same way. On the last day of a month the window before already is that whole month: the
 * two reads share one key, so the server is asked once. "Exportar CSV" (owner 2026-10-19, the admin
 * module's button handed in as `parts`) downloads the month so far on screen through the report's
 * export twin.
 */

import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { orgActivityKey } from "../../../shared/api/queryKeys";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { monthToDate } from "../../../shared/ui/reportWindows";
import type { ActivityReportView, ReportWindow } from "../../../types/reports";
import { ORG_EXPORT_NAMES, downloadOrgReport, fetchOrgActivity } from "../api/orgReportsApi";
import { ActivityTiles } from "../components/ActivityTiles";
import { previousWindow, wholeMonthBefore } from "../model/previousWindow";
import type { ReportParts } from "./reportParts";

export interface ActivityTilesContainerProps {
  readonly organizationId: string;
  /** The day "this month" ends on. */
  readonly today: Date;
  /** The parts another module draws ("Exportar CSV"). */
  readonly parts: Pick<ReportParts, "ExportCsv">;
}

function useActivity(organizationId: string, window: ReportWindow) {
  return useQuery<ActivityReportView, ApiError>({
    queryKey: orgActivityKey(organizationId, window.from, window.to),
    queryFn: ({ signal }) => fetchOrgActivity(organizationId, window, signal),
  });
}

export function ActivityTilesContainer({ organizationId, today, parts }: ActivityTilesContainerProps): ReactElement {
  const { ExportCsv } = parts;
  const month = monthToDate(today);
  const current = useActivity(organizationId, month);
  const previous = useActivity(organizationId, previousWindow(month));
  const wholePrevious = useActivity(organizationId, wholeMonthBefore(month));

  if (current.isError) {
    return (
      <ErrorState
        headingLevel={3}
        error={asApiError(current.error)}
        onRetry={() => {
          void current.refetch();
          if (previous.isError) void previous.refetch();
          if (wholePrevious.isError) void wholePrevious.refetch();
        }}
      />
    );
  }
  if (current.data === undefined) return <TableSkeleton />;
  return (
    <ActivityTiles
      current={current.data}
      previous={previous.data}
      wholePrevious={wholePrevious.data}
      actions={<ExportCsv download={() => downloadOrgReport(organizationId, "activity", month)} fallbackName={ORG_EXPORT_NAMES.activity} />}
    />
  );
}
